import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { Icon, type IconType } from "@trimble-creative-strategy/trimble-mxp-icons";
import { TerraLink } from "../TerraLink/TerraLink";
import { CleanDescription } from "../CommonDescription/CommonDescription";
import "./CardEcommerce.css";
import { renderIcon, copyToClipboard } from "./CardEcommerceMethods";
export interface CardEcommerceItem {
  icon?: IconType;
  text?: string;
}
type DataObject = Record<string, any>;
interface MissingItem {
  object: DataObject;
  index: number;
  missing: string[];
}
interface ContentObject {
  missingFields?: MissingItem[];
  completeFields?: DataObject[];
}
export interface CardEcommerce {
  header?: string;
  subheader?: string;
  description?: string | React.ReactNode;
  cardType?: "light" | "dark" | "normal";
  details?: CardEcommerceItem[];
  button?: TerraLink;
  priceDescription?: string;
  detailsHeader?: string;
  featured?: boolean;
  featuredText?: string;
  currency?: string;
  discount?: string;
  discountCode?: string;
  crossedOutPrice?: string;
  index?: number;
  indexAnimation?: string | undefined;
  LinkComponent?: React.Component<any, any>;
  validatingCardDataTop?: ContentObject;
  validatingCardDataBottom?: ContentObject;
  validatingCardDataListSectionBottom?: ContentObject;
  longDetailHeader?: boolean;
  /** When this changes (e.g. "monthly" | "annual"), the price subheader remounts so the slide-in animation runs. */
  subscriptionSelection?: string;
  id?: string;
  viewMoreLabel?: string;
  viewLessLabel?: string;
}
export const CardEcommerce = React.memo(t0 => {
  const $ = _c(55);
  const {
    header,
    subheader,
    description,
    cardType,
    details,
    button,
    priceDescription,
    detailsHeader,
    featured,
    featuredText,
    currency,
    discount,
    discountCode,
    crossedOutPrice,
    index,
    indexAnimation,
    validatingCardDataTop,
    validatingCardDataBottom,
    validatingCardDataListSectionBottom,
    longDetailHeader,
    subscriptionSelection,
    id,
    viewMoreLabel,
    viewLessLabel
  } = t0;
  const [clicked, setClicked] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const cardMode = cardType || "normal";
  const isDark = cardMode !== "normal";
  const innerCardClasses = isDark ? "p-[56px_32px] bg-gray-m10 rounded-[10px] shadow-none h-full" : "p-[56px_32px] bg-white rounded-[10px] shadow-[0px_110px_31px_0px_rgba(0,0,0,0),0px_70px_28px_0px_rgba(0,0,0,0.01),0px_40px_24px_0px_rgba(0,0,0,0.05),0px_18px_18px_0px_rgba(0,0,0,0.09),0px_4px_10px_0px_rgba(0,0,0,0.1)] h-full";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const getAllMissingFields = function getAllMissingFields(cardDataWithMissingAndValidCards) {
      if (!cardDataWithMissingAndValidCards.length) {
        return [];
      }
      const longestArray = cardDataWithMissingAndValidCards.reduce(_temp);
      return longestArray;
    };
    t1 = content => {
      if (content?.missingFields?.length) {
        const formatMissingData = content?.missingFields.map(_temp2);
        const listOfFields = getAllMissingFields(formatMissingData);
        if (listOfFields?.length && content?.completeFields?.length) {
          return true;
        } else {
          if (listOfFields?.length && !content?.completeFields?.length) {
            return false;
          } else {
            return true;
          }
        }
      }
      return true;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const checkForMissingContentToCalculateHeight = t1;
  const standardHeightTop = checkForMissingContentToCalculateHeight(validatingCardDataTop);
  const standardHeightBottom = checkForMissingContentToCalculateHeight(validatingCardDataBottom);
  const standardHeightListHeader = checkForMissingContentToCalculateHeight(validatingCardDataListSectionBottom);
  const headerColor = isDark ? "text-white" : "text-gray-m9";
  const priceColor = isDark ? "text-yellow-m10" : "text-gray-m9";
  const crossedOutColor = isDark ? "text-gray-m5" : "text-gray-m6";
  const descriptionColor = isDark ? "text-white" : "text-gray-m9";
  const detailColor = isDark ? "text-white fill-white" : "text-gray-m9 fill-gray-m9";
  const discountTextClasses = isDark ? "text-green-m2 bg-green-m15" : "text-green-m15 bg-green-m2";
  const discountCodeClasses = isDark ? "bg-green-m2 text-green-m14" : "bg-green-m15 text-green-m2";
  let t2;
  if ($[1] !== isDark) {
    t2 = !isDark ? {
      backgroundColor: "#ffffff"
    } : {
      border: "1px solid white",
      backgroundColor: "var(--color-gray-m10)"
    };
    $[1] = isDark;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = featured ? `${"m-0 relative bottom-14 -mx-8 w-[calc(100%+64px)] h-8 flex items-center justify-center text-base uppercase font-bold tracking-[0.8px] rounded-t-[10px]"} ${isDark ? "bg-white text-gray-m10" : "bg-blue-trmb text-white"}` : "m-0 relative bottom-14 -mx-8 w-[calc(100%+64px)] h-8 flex items-center justify-center text-base uppercase font-bold tracking-[0.8px] rounded-t-[10px] bg-white invisible";
  let t4;
  if ($[3] !== featuredText || $[4] !== t3) {
    t4 = <p className={t3}>{featuredText}</p>;
    $[3] = featuredText;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== cardMode || $[7] !== clicked || $[8] !== crossedOutColor || $[9] !== crossedOutPrice || $[10] !== currency || $[11] !== discount || $[12] !== discountCode || $[13] !== discountCodeClasses || $[14] !== discountTextClasses || $[15] !== header || $[16] !== headerColor || $[17] !== index || $[18] !== indexAnimation || $[19] !== priceColor || $[20] !== priceDescription || $[21] !== standardHeightTop || $[22] !== subheader || $[23] !== subscriptionSelection) {
    t5 = (header || subheader) && <div className="mb-[25px] flex flex-col min-h-[205px]" style={standardHeightTop === false ? {
      minHeight: "125px"
    } : undefined}>{header && <h3 className={`grow ${headerColor} text-[2.5rem] not-italic font-light leading-[3rem] tracking-[0.8px]`}>{header}</h3>}{discount && <section className="[animation:slide-up_0.2s_linear] -mt-10 mb-2 flex w-fit max-w-full items-center max-[599px]:mt-2" key={`${index}-${indexAnimation}-discount`} onClick={() => discountCode && copyToClipboard(discountCode, setClicked)}><div className={`inline-flex h-9 max-w-full items-center gap-2 rounded-lg px-2 py-1 text-xs font-extrabold uppercase tracking-[0.24px] ${discountTextClasses}`}><span className="whitespace-nowrap">{discount}</span>{discountCode && <><span className="inline-flex shrink-0 items-center">{renderIcon(clicked, cardMode)}</span><span className={`cursor-copy whitespace-nowrap rounded-lg px-1.5 py-0.5 text-xs ${discountCodeClasses}`}>{discountCode}</span></>}</div></section>}{subheader && <div className="flex items-end mt-2 [animation:slide-in_0.35s_ease-out_normal]" key={`${index}-${indexAnimation}-price-${subscriptionSelection ?? ""}`}>{crossedOutPrice && <p className={`text-[1.875rem] not-italic font-bold leading-[2.125rem] tracking-[0.6px] m-0 uppercase line-through decoration-[1px] mr-3 ${crossedOutColor}`}>{crossedOutPrice}</p>}<p className={`text-[1.875rem] not-italic font-bold leading-[2.125rem] tracking-[0.6px] m-0 uppercase ${priceColor}`}>{subheader}</p><p className={`m-0 ml-0.5 text-base font-semibold leading-[1.375rem] uppercase ${headerColor}`}>{currency}</p></div>}{subheader && priceDescription && <p className={`m-0 ml-0.5 text-base font-semibold leading-[1.375rem] ${headerColor}`}>{priceDescription}</p>}</div>;
    $[6] = cardMode;
    $[7] = clicked;
    $[8] = crossedOutColor;
    $[9] = crossedOutPrice;
    $[10] = currency;
    $[11] = discount;
    $[12] = discountCode;
    $[13] = discountCodeClasses;
    $[14] = discountTextClasses;
    $[15] = header;
    $[16] = headerColor;
    $[17] = index;
    $[18] = indexAnimation;
    $[19] = priceColor;
    $[20] = priceDescription;
    $[21] = standardHeightTop;
    $[22] = subheader;
    $[23] = subscriptionSelection;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== button) {
    t6 = button?.label && <section className="w-full max-h-[60px]! mb-6 flex"><TerraLink {...button} destination={button?.destination} size="medium" className="w-[336px] max-h-[60px]!" primary={true} /></section>;
    $[25] = button;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== description || $[28] !== descriptionColor || $[29] !== detailColor || $[30] !== details || $[31] !== detailsExpanded || $[32] !== detailsHeader || $[33] !== header || $[34] !== headerColor || $[35] !== id || $[36] !== index || $[37] !== longDetailHeader || $[38] !== standardHeightBottom || $[39] !== standardHeightListHeader || $[40] !== subheader || $[41] !== viewLessLabel || $[42] !== viewMoreLabel) {
    t7 = (header || subheader) && <div className="flex flex-col min-h-[50px] max-h-[600px]">{standardHeightBottom && <div className={`flex flex-col min-h-24 min-[600px]:min-h-[150px] ${descriptionColor} mb-8 text-base leading-7 font-normal flex-grow`}><CleanDescription description={description} className="flex-grow flex flex-col [&_p]:mb-[50px] [&_p]:text-[1.125rem]! [&_p]:leading-8! [&_p]:tracking-[0.18px]! [&_p]:m-0 [&_p]:flex-grow" /></div>}<section className="flex flex-col"><div className="flex-grow pb-0 mb-0 max-w-[336px]">{details && details.length > 0 && <><div id={`pricing-card-details-${id ?? index}`} className={detailsExpanded ? "" : "max-[599px]:hidden"}>{standardHeightListHeader && <p className={`min-h-7 text-[1.375rem] font-bold leading-7 mb-6 ${headerColor}`} style={longDetailHeader ? {
                minHeight: "56px"
              } : {
                minHeight: "28px"
              }}>{detailsHeader}</p>}{details.map((item, index_0) => <div key={`${item.text}-${index_0}`} className={`flex items-start ${detailColor} text-[1.125rem] font-normal leading-8 tracking-[0.01em] text-left mb-[5px] [&_svg]:mt-2 [&_svg]:w-[18px] [&_svg]:mr-2.5`}>{item.icon && <Icon type={item.icon} />}{!item.icon && <span className="inline-block w-[6px] h-[6px] rounded-full bg-current shrink-0 mt-[13px] mr-2.5" />}{item.text && <span>{item.text}</span>}</div>)}</div><button type="button" onClick={() => setDetailsExpanded(_temp3)} aria-expanded={detailsExpanded} aria-controls={`pricing-card-details-${id ?? index}`} className={`hidden max-[599px]:flex items-center gap-1 bg-transparent border-0 p-0 mt-4 cursor-pointer text-[1.125rem] font-bold leading-8 tracking-[0.01em] underline ${detailColor}`}>{detailsExpanded ? viewLessLabel || "View less" : viewMoreLabel || "View more"}<Icon type="t2u-chevron-right" rotateDeg={detailsExpanded ? "270" : "90"} size={12} /></button></>}</div></section></div>;
    $[27] = description;
    $[28] = descriptionColor;
    $[29] = detailColor;
    $[30] = details;
    $[31] = detailsExpanded;
    $[32] = detailsHeader;
    $[33] = header;
    $[34] = headerColor;
    $[35] = id;
    $[36] = index;
    $[37] = longDetailHeader;
    $[38] = standardHeightBottom;
    $[39] = standardHeightListHeader;
    $[40] = subheader;
    $[41] = viewLessLabel;
    $[42] = viewMoreLabel;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== t5 || $[45] !== t6 || $[46] !== t7) {
    t8 = <div className="flex flex-col h-full max-w-[336px] relative bottom-[25px]"><div className="flex-[0_1_0px] h-full flex-grow max-w-[336px]">{t5}{t6}{t7}</div></div>;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  let t9;
  if ($[48] !== innerCardClasses || $[49] !== t4 || $[50] !== t8) {
    t9 = <div className={innerCardClasses}>{t4}{t8}</div>;
    $[48] = innerCardClasses;
    $[49] = t4;
    $[50] = t8;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== t2 || $[53] !== t9) {
    t10 = <section className="max-w-[400px]! shadow-[0px_110px_31px_0px_rgba(0,0,0,0),0px_70px_28px_0px_rgba(0,0,0,0.01),0px_40px_24px_0px_rgba(0,0,0,0.05),0px_18px_18px_0px_rgba(0,0,0,0.09),0px_4px_10px_0px_rgba(0,0,0,0.1)] rounded-[10px] overflow-hidden h-full max-[599px]:w-[342px]" style={t2}>{t9}</section>;
    $[52] = t2;
    $[53] = t9;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  return t10;
});
CardEcommerce.displayName = "CardEcommerce";
function _temp(longest, current) {
  return current.length > longest.length ? current : longest;
}
function _temp2(missing) {
  return missing?.missing;
}
function _temp3(prev) {
  return !prev;
}